import $ from 'jquery';
import { Component } from '@mycolorway/tao';
import { matches, closest } from 'next/javascripts/modules/dom';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import 'next/stylesheets/components/grid/subtodo-row.scss';

Component('tr-grid-subtodo-row', {
  mixins: [eventListenerMixin],

  properties: {
    guid: {
      type: String,
      observer: 'guidChanged',
    },

    creationUrl: String,
    editUrl: String,
    completionStatusUrl: String,
    detailUrl: String,
    deleteUrl: String,

    completed: {
      type: Boolean,
      default: false,
      observer: 'completedChanged',
    },

    running: {
      type: Boolean,
      default: false,
      observer: 'runningChanged',
    },

    editing: {
      type: Boolean,
      default: false,
    },

    sortable: {
      type: Boolean,
      default: false,
    },

    saving: {
      type: Boolean,
      default: false,
      observer: 'savingChanged',
    },
  },

  connected() {
    this.pendingChanges = {};

    this.listenTo(this, {
      'tao:cellEditingChange': this.handleCellEditingChange.bind(this),
      'tao:cellValueChange': this.handleCellValueChange.bind(this),
      'tr-grid-todo-title-cell:escape': () => {
        if (!this.guid) this.parentNode.removeChild(this);
      },
      'tr-grid-todo-title-cell:backspace': this.handleTodoTitleBackspace.bind(this),
      'tr-grid-todo-title-cell:completedChange': this.handleCompletionStatusChange.bind(this),
      'tr-menu:opened': () => { this.editing = true; },
      'tr-menu:closed': () => { this.editing = false; },
      click: this.handleClick.bind(this),
    });

    if (!this.guid) {
      document.addEventListener('mousedown', this.handleMouseDown.bind(this));
    }
  },

  handleMouseDown(event) {
    const { target } = event;
    const title = this.querySelector('tr-grid-todo-title-cell textarea').value;
    const mouseDownInRow = this.contains(target) || closest(target, '.grid-cell-editor-menu', this);
    if (!mouseDownInRow
      && !title
      && !closest(target, '.link-new-subtodo')) {
      this.remove();
    }
  },

  handleClick(event) {
    if (this.guid && !this.editing) {
      this.selected = true;

      const { target } = event;
      if (closest(target, '.tr-todo-assignee-select-field')
        || closest(target, '.tr-todo-date-field')
        || closest(target, '.actions-cell .actions-menu')
        || closest(target, '.completion-status-checkbox')) return;

      this.namespacedTrigger('openTodo', {
        bubbles: true,
        detail: {
          detailUrl: this.detailUrl,
          openInTab: window.tower.metaKey(event),
        },
      });
    }
  },

  handleCellEditingChange(event) {
    this.editing = event.detail || Array.from(this.children)
      .some(child => matches(child, '.grid-cell') && child.editing);
  },

  ready() {
    this.titleCell = this.querySelector('tr-grid-todo-title-cell');
  },

  disconnected() {
    this.stopListening();
  },

  handleCellValueChange(event) {
    const { target } = event;

    if (this.guid) {
      if (target.key !== 'content') {
        this.updateTodo({ [target.key]: target.value });
      } else if (target.value) {
        target.disabled = true;
        this.updateTodo({ [target.key]: target.value });
      } else {
        target.editing = false;
      }
    } else if (target.key === 'content' && target.value) {
      if (this.creationUrl) {
        target.disabled = true;
        this.createNewTodo(target.value);
      }
    } else if (this.todoCreationTask) {
      this.pendingChanges[target.key] = target.value;
    }
  },

  createNewTodo(content) {
    const data = {};

    Array.from(this.children).forEach((child) => {
      if (matches(child, '.grid-cell')) {
        data[child.key] = child.value;
      }
    });

    data.content = content;
    this.saving = true;

    this.todoCreationTask = $.ajax({
      url: this.creationUrl,
      method: 'post',
      data: {
        todos_creation: data,
        tao_id: this.taoId,
      },
      dataType: 'script',
    }).then(() => {
      this.todoCreationTask = null;
      this.saving = false;
    });
  },

  updateTodo(params = {}) {
    this.saving = true;

    $.ajax({
      url: this.editUrl,
      method: 'patch',
      data: {
        todos_management: params,
      },
      dataType: 'script',
    }).then(() => {
      this.saving = false;
      this.namespacedTrigger('update', {
        detail: { guid: this.guid, params },
        bubbles: true,
      });
    });
  },

  deleteTodo() {
    this.style.display = 'none';

    $.ajax({
      url: this.deleteUrl,
      method: 'delete',
      dataType: 'script',
    });
  },

  guidChanged(newGuid, oldGuid) {
    if (!oldGuid && newGuid) {
      document.removeEventListener('mousedown', this.handleMouseDown);
      if (Object.keys(this.pendingChanges).length > 0) {
        this.updateTodo(this.pendingChanges);
        this.pendingChanges = {};
      }
    }
  },

  completedChanged(completed) {
    if (this.titleCell && this.titleCell.completed !== completed) {
      this.titleCell.completed = completed;
    }

    this.namespacedTrigger('completed', {
      detail: { guid: this.guid, completed },
      bubbles: true,
    });
  },

  runningChanged(running) {
    if (this.titleCell && this.titleCell.running !== running) {
      this.titleCell.running = running;
    }

    this.namespacedTrigger('running', {
      detail: { guid: this.guid, running },
      bubbles: true,
    });
  },

  handleTodoTitleBackspace() {
    if (!this.guid) {
      this.parentNode.removeChild(this);
    } else {
      this.deleteTodo();
    }
  },

  handleCompletionStatusChange() {
    if (this.completionStatusUrl && !this.completionRequest) {
      this.completionRequest = $.ajax({
        url: this.completionStatusUrl,
        method: 'post',
        dataType: 'script',
      }).then(() => {
        this.completionRequest = null;
      });
    }
  },

  savingChanged(isSaving) {
    this.titleCell.disabled = isSaving;
  },
});
